// json.ts is the JSON-convenience layer over the byte-oriented data /
// userdata surfaces. The *JSON methods (getJSON / putJSON / putJSONIfMatch)
// are pure compositions of the existing get / put / putIfMatch — they
// serialize on the way in, parse on the way out, and pin the content-type
// to "application/json". No new network or storage path: a putJSON is a
// put, a getJSON is a get + JSON.parse.
//
// The point of these helpers is ergonomics, not a new feature. The common
// AI-generated glow holds one in-memory state object and saves it whole;
// without these it must hand-stringify/parse and remember to pass
// "application/json" every time (and, for the safe shared-write path,
// thread the crc32c through a CAS loop by hand). getJSON returns the crc
// alongside the parsed value precisely so that loop is one step.

import { BacklitError } from "./errors.js";
import type { BatchEntry, ListPage } from "./backend.js";
import { OPID, registerRebase } from "./offline-conflict.js";

// JSON_CONTENT_TYPE is the content-type every *JSON write stores under.
// It is on the server's default allowlist (CLAUDE.md §8).
export const JSON_CONTENT_TYPE = "application/json";

// JSONResult is what getJSON resolves to: the parsed value plus the
// body's crc32c. The crc is included (not just the value) so the result
// drops straight into putJSONIfMatch as the expected crc — the whole
// load → edit → compare-and-set loop without a second round trip. T
// defaults to unknown; callers pass their own shape (getJSON<MyState>).
// The offline tags (fromCache / pending — see backend.ts BlobResult) pass
// through from the underlying get, set only when true.
export type JSONResult<T> = { value: T; crc32c: string; fromCache?: true; pending?: true };

// ReadTags is the pair of offline tags a result may carry.
export type ReadTags = { fromCache?: true; pending?: true };

// carryTags copies a result's offline tags onto the JSON result built from it
// and returns that result: fromCache / pending only when exactly true (so a
// result never carries `fromCache: undefined` — exactOptionalPropertyTypes),
// a device-answered page's boolean complete (so getBatchJSON keeps it), and a
// queued CAS result's op id (the OPID symbol) non-enumerably, which
// updateJSON reads to register its rebase closure.
export function carryTags<T extends object>(from: object, to: T): T {
  const f = from as ReadTags & { complete?: unknown; [OPID]?: unknown };
  const t = to as ReadTags & { complete?: boolean };
  if (f.fromCache === true) t.fromCache = true;
  if (f.pending === true) t.pending = true;
  if (typeof f.complete === "boolean") t.complete = f.complete;
  if (f[OPID]) Object.defineProperty(to, OPID, { value: f[OPID] });
  return to;
}

// committed is updateJSON's result for a matched CAS r: pending carried over,
// the op id not (it was consumed by the registration).
function committed<V>(r: { crc32c: string; pending?: true; [OPID]?: { opId: string; slot: number } }, value: V, mutate: unknown, maxAttempts: number) {
  const t = r[OPID];
  if (t) registerRebase(t.opId, t.slot, mutate as (v: unknown) => unknown, maxAttempts);
  return { value, crc32c: r.crc32c, ...(r.pending ? { pending: true as const } : {}) };
}

// PutJSONIfMatchResult is the JSON analogue of PutIfMatchResult. A
// mismatch is a NORMAL result, not an error: the witness carries the
// current value already parsed (a JSONResult<T>), so a retry loop reads
// witness.value directly instead of re-parsing a Blob.
export type PutJSONIfMatchResult<T> =
  | { matched: true; crc32c: string; pending?: true }
  | { matched: false; witness: JSONResult<T> };

// Records-surface JSON result types (window.backlit.records): the data/userdata
// JSON shapes plus the record's owner uid (CLAUDE.md §8 D7). RecordJSONResult is
// what records.getJSON / updateJSON resolve to; RecordPutJSONIfMatchResult is
// the records putJSONIfMatch outcome — matched carries the committed owner (the
// 200 X-Backlit-Record-Owner header), the witness carries it on a mismatch.
export type RecordJSONResult<T> = JSONResult<T> & { owner: string };

export type RecordPutJSONIfMatchResult<T> =
  | { matched: true; crc32c: string; owner: string; pending?: true }
  | { matched: false; witness: RecordJSONResult<T> };

// encodeJSON serializes value to a JSON string body, raising the SDK's
// own BacklitError (never a raw TypeError) so every *JSON method honours
// the "everything throws BacklitError" contract.
//
// JSON.stringify has two non-string outcomes we must catch:
//   - it throws TypeError on a circular reference or a BigInt;
//   - it RETURNS undefined for a bare undefined / function / symbol.
// Both are caller mistakes → invalid_argument (client-side, status 0).
export function encodeJSON(value: unknown): string {
  let text: string | undefined;
  try {
    text = JSON.stringify(value);
  } catch (e) {
    throw new BacklitError(
      "invalid_argument",
      "value is not JSON-serializable: " + (e instanceof Error ? e.message : String(e)),
      0,
      e,
    );
  }
  if (typeof text !== "string") {
    // JSON.stringify(undefined | (()=>{}) | Symbol()) === undefined.
    throw new BacklitError(
      "invalid_argument",
      "value is not JSON-serializable (a bare undefined, function, or symbol has no JSON form)",
    );
  }
  return text;
}

// decodeJSON parses a stored Blob body as JSON. A parse failure means the
// bytes at this key are not JSON (e.g. the key was written with a
// different content-type via the byte-level put) — surfaced as
// invalid_body so the caller can distinguish "stored value isn't JSON"
// from a genuine miss (not_found). key is interpolated into the message
// for a debuggable error.
export async function decodeJSON<T>(blob: Blob, key: string): Promise<T> {
  const text = await blob.text();
  try {
    return JSON.parse(text) as T;
  } catch (e) {
    throw new BacklitError(
      "invalid_body",
      `the value stored at "${key}" is not valid JSON`,
      0,
      e,
    );
  }
}

// BatchJSONEntry is one row of a getBatchJSON() page: the list metadata plus,
// on the INCLUDED arm, the JSON-parsed value (T). It mirrors BatchEntry's
// discriminated shape (narrow on `.omitted`), but the value is the parsed T
// rather than a Blob. Server-omitted entries pass through with their wire
// `reason` (e.g. "too_large"); an included value that fails to JSON.parse
// degrades to the omitted arm with the SDK-synthesized reason "not_json"
// (never a wire value). A JSON null/0/""/false parses to a legal INCLUDED
// value — the discriminant is `omitted`, never value-nullness. One T per page.
// An offline answer (backlit.offline) may add the SDK-side reason "not_cached"
// (listed, but the bytes are not on this device) and a row-level `pending`.
export type BatchJSONEntry<T> = {
  readonly key: string;
  readonly crc32c: string;
  readonly contentType: string;
  readonly size: number;
  readonly pending?: true;
} & (
  | { readonly omitted?: false; readonly value: T }
  | { readonly omitted: true; readonly value?: never; readonly reason?: string }
);

// RecordBatchJSONEntry is the records getBatchJSON() row: BatchJSONEntry plus
// the record's owner uid.
export type RecordBatchJSONEntry<T> = BatchJSONEntry<T> & { readonly owner: string };

// decodeBatchJSONPage is the shared parse-and-degrade layer behind
// data/userdata/records getBatchJSON(). It composes over a getBatch() result:
// for each INCLUDED entry it attempts JSON.parse (content-type-agnostic —
// getJSON parity); a parse failure degrades that one entry to the omitted arm
// with reason "not_json" (SDK-synthesized, never on the wire). Server-omitted
// entries pass through untouched, carrying their wire `reason`. It is
// parameterised only over the entry type so data/userdata (BatchEntry) and
// records (RecordBatchEntry, which the metadata spread preserves via `owner`)
// share one implementation, mirroring how decodeJSON serves every namespace.
export async function decodeBatchJSONPage<T, E extends BatchEntry>(
  page: ListPage<E>,
): Promise<ListPage<BatchJSONEntry<T>>> {
  const items: BatchJSONEntry<T>[] = [];
  for (const entry of page.items) {
    // The metadata fields common to both arms (records carries `owner` too;
    // spreading the whole entry keeps it without a records-specific branch).
    const { value, omitted, reason, ...rest } = entry as BatchEntry & {
      value?: Blob;
      omitted?: boolean;
      reason?: string;
    };
    if (omitted === true) {
      // Server-omitted: pass through with its wire reason, no parse attempted.
      items.push({ ...rest, omitted: true, ...(reason !== undefined ? { reason } : {}) } as unknown as BatchJSONEntry<T>);
      continue;
    }
    // INCLUDED: attempt JSON.parse on the value's text (content-type-agnostic).
    const text = await (value as Blob).text();
    try {
      const parsed = JSON.parse(text) as T;
      items.push({ ...rest, value: parsed } as unknown as BatchJSONEntry<T>);
    } catch {
      items.push({ ...rest, omitted: true, reason: "not_json" } as unknown as BatchJSONEntry<T>);
    }
  }
  return carryTags(page, { items, next: page.next });
}

const DEFAULT_UPDATE_ATTEMPTS = 6;

export interface UpdateJSONOptions {
  // Max compare-and-swap attempts before giving up under contention.
  // Default 6.
  maxAttempts?: number;
}

// updateBackoff spaces out retries so concurrent writers don't re-collide in
// lockstep. The loop is already network-paced (each attempt is a full
// putJSONIfMatch round trip), so this stays tens of ms at most, with jitter.
function updateBackoff(attempt: number): Promise<void> {
  const base = Math.min(50, 5 * (attempt + 1));
  const ms = base * (0.5 + Math.random());
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// runUpdateJSON is the read-modify-write retry loop behind
// data.updateJSON / userdata.updateJSON. It loads the current value, applies
// `mutate`, and commits with a compare-and-swap; on a concurrent write it
// rebases onto the witness (already parsed) and retries. It is parameterised
// over a namespace's getJSON + putJSONIfMatch so both namespaces share one
// implementation (and userdata inherits its `unauthenticated` rewrite).
//
// `mutate` MUST be replayable: under contention it runs again on each
// conflict against the fresher witness value, so it must be a pure function
// of its input (no side effects, no captured one-shot deltas). On an absent
// key it receives `undefined` and the first write is create-only.
//
// Throws BacklitError(code:"conflict") if it can't win the race within
// maxAttempts — a sign of extreme contention, not a normal outcome.
//
// A commit queued offline (backlit.offline) resolves pending:true and
// registers `mutate` in this tab's rebase registry (offline-conflict.ts), so a
// conflict met when the queue drains can re-run it on the newer value.
export async function runUpdateJSON<T>(
  getJSON: (key: string) => Promise<JSONResult<T>>,
  putJSONIfMatch: (key: string, value: unknown, expectedCrc32c: string) => Promise<PutJSONIfMatchResult<T>>,
  key: string,
  mutate: (current: T | undefined) => T,
  options?: UpdateJSONOptions,
): Promise<JSONResult<T>> {
  const maxAttempts = options?.maxAttempts ?? DEFAULT_UPDATE_ATTEMPTS;

  // Initial load. An absent key → undefined current + create-only ("").
  let current: T | undefined;
  let expected: string;
  try {
    const got = await getJSON(key);
    current = got.value;
    expected = got.crc32c;
  } catch (e) {
    if (e instanceof BacklitError && e.code === "not_found") {
      current = undefined;
      expected = "";
    } else {
      throw e; // invalid_key, unauthenticated, network, … propagate as-is
    }
  }

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const next = mutate(current);
    const r = await putJSONIfMatch(key, next, expected);
    if (r.matched) return committed(r, next, mutate, maxAttempts);
    // A peer wrote first — rebase onto the witness (a freshly parsed object)
    // and retry. No clone needed: the witness is a new object each round.
    current = r.witness.value;
    expected = r.witness.crc32c;
    if (attempt < maxAttempts - 1) await updateBackoff(attempt);
  }
  throw new BacklitError(
    "conflict",
    `updateJSON("${key}") gave up after ${maxAttempts} attempts under write contention`,
  );
}

// runUpdateJSONWithOwner is the records-surface sibling of runUpdateJSON: the
// same load → mutate → compare-and-swap → rebase-and-retry loop, but the
// committed result carries the record's owner uid (from the matched commit's
// header). It is a SEPARATE function (runUpdateJSON stays byte-identical for
// data/userdata) parameterised over the records getJSON + putJSONIfMatch.
//
// Error semantics match runUpdateJSON except `forbidden` MUST propagate, not
// loop: only the INITIAL load's not_found is swallowed (→ create-only first
// write). A `forbidden` thrown by the first putJSONIfMatch (a non-creator/
// non-admin writing a foreign record) rejects the whole updateJSON after
// exactly one attempt — it is the server's hard ownership boundary, not a
// retryable conflict.
export async function runUpdateJSONWithOwner<T>(
  getJSON: (key: string) => Promise<RecordJSONResult<T>>,
  putJSONIfMatch: (key: string, value: unknown, expectedCrc32c: string) => Promise<RecordPutJSONIfMatchResult<T>>,
  key: string,
  mutate: (current: T | undefined) => T,
  options?: UpdateJSONOptions,
): Promise<RecordJSONResult<T>> {
  const maxAttempts = options?.maxAttempts ?? DEFAULT_UPDATE_ATTEMPTS;

  // Initial load. An absent key → undefined current + create-only (""). Only
  // not_found is swallowed here; forbidden/unauthenticated/network propagate.
  let current: T | undefined;
  let expected: string;
  try {
    const got = await getJSON(key);
    current = got.value;
    expected = got.crc32c;
  } catch (e) {
    if (e instanceof BacklitError && e.code === "not_found") {
      current = undefined;
      expected = "";
    } else {
      throw e;
    }
  }

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const next = mutate(current);
    // A thrown forbidden (or any non-mismatch error) propagates here — the loop
    // only continues on a { matched: false } witness.
    const r = await putJSONIfMatch(key, next, expected);
    if (r.matched) return { ...committed(r, next, mutate, maxAttempts), owner: r.owner };
    current = r.witness.value;
    expected = r.witness.crc32c;
    if (attempt < maxAttempts - 1) await updateBackoff(attempt);
  }
  throw new BacklitError(
    "conflict",
    `updateJSON("${key}") gave up after ${maxAttempts} attempts under write contention`,
  );
}
